import { createContext, useContext, useReducer, useEffect, useState, type ReactNode } from 'react';
import type { CartItem, Product } from '../types';
import type { CouponAPI } from '../api/client';

interface CartState {
  items: CartItem[];
  lastAdded: { product: Product; timestamp: number } | null;
}

type CartAction =
  | { type: 'ADD_ITEM'; product: Product }
  | { type: 'REMOVE_ITEM'; productId: string }
  | { type: 'UPDATE_QUANTITY'; productId: string; quantity: number }
  | { type: 'CLEAR_CART' }
  | { type: 'HYDRATE_CART'; items: CartItem[] };

interface CartContextType {
  state: CartState;
  dispatch: React.Dispatch<CartAction>;
  totalItems: number;
  totalPrice: number;
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, qty: number) => void;
  clearCart: () => void;
  appliedCoupon: CouponAPI | null;
  discountAmount: number;
  setAppliedCoupon: (coupon: CouponAPI | null, discount: number) => void;
  removeCoupon: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD_ITEM': {
      const maxStock = action.product.stockQuantity ?? 100;
      const existing = state.items.find((i) => i.product.id === action.product.id);
      if (existing) {
        if (existing.quantity >= maxStock) return state;
        return {
          ...state,
          lastAdded: { product: action.product, timestamp: Date.now() },
          items: state.items.map((i) =>
            i.product.id === action.product.id
              ? { ...i, quantity: Math.min(i.quantity + 1, maxStock) }
              : i
          ),
        };
      }
      if (maxStock <= 0) return state;
      return {
        ...state,
        lastAdded: { product: action.product, timestamp: Date.now() },
        items: [...state.items, { product: action.product, quantity: 1 }],
      };
    }
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter((i) => i.product.id !== action.productId),
      };
    case 'UPDATE_QUANTITY': {
      if (action.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter((i) => i.product.id !== action.productId),
        };
      }
      const item = state.items.find((i) => i.product.id === action.productId);
      const stock = item?.product.stockQuantity ?? 100;
      return {
        ...state,
        items: state.items.map((i) =>
          i.product.id === action.productId
            ? { ...i, quantity: Math.min(action.quantity, stock) }
            : i
        ),
      };
    }
    case 'CLEAR_CART':
      return { ...state, items: [], lastAdded: null };
    case 'HYDRATE_CART':
      // Replaces in-memory items with persisted ones on client mount.
      return { ...state, items: action.items };
    default:
      return state;
  }
}

const isBrowser = typeof window !== 'undefined';

function loadCart(): CartItem[] {
  if (!isBrowser) return [];
  try {
    const raw = window.localStorage.getItem('zs_cart');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const COUPON_KEY = 'zs_coupon';

function loadCouponState(): { coupon: CouponAPI | null; amount: number } {
  if (!isBrowser) return { coupon: null, amount: 0 };
  try {
    const raw = sessionStorage.getItem(COUPON_KEY);
    return raw ? JSON.parse(raw) : { coupon: null, amount: 0 };
  } catch { return { coupon: null, amount: 0 }; }
}

export function CartProvider({ children }: { children: ReactNode }) {
  // SSR-safe init: start empty on both server and client first render so
  // the hydrated HTML matches. Real cart loads in the effect below on
  // client mount. Cart-count badge will briefly show 0 then update — the
  // ~16ms flicker is acceptable.
  const [state, dispatch] = useReducer(cartReducer, { items: [], lastAdded: null });

  // Load persisted cart on client mount (post-hydration).
  useEffect(() => {
    const items = loadCart();
    if (items.length > 0) {
      dispatch({ type: 'HYDRATE_CART', items });
    }
  }, []);

  useEffect(() => {
    if (!isBrowser) return;
    window.localStorage.setItem('zs_cart', JSON.stringify(state.items));
  }, [state.items]);

  const totalItems = state.items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = state.items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

  const [appliedCoupon, setAppliedCouponRaw] = useState<CouponAPI | null>(() => loadCouponState().coupon);
  const [discountAmount, setDiscountAmountRaw] = useState<number>(() => loadCouponState().amount);

  const setAppliedCoupon = (coupon: CouponAPI | null, discount: number) => {
    setAppliedCouponRaw(coupon);
    setDiscountAmountRaw(discount);
    if (!isBrowser) return;
    if (coupon) {
      try { sessionStorage.setItem(COUPON_KEY, JSON.stringify({ coupon, amount: discount })); } catch {}
    } else {
      sessionStorage.removeItem(COUPON_KEY);
    }
  };
  const removeCoupon = () => setAppliedCoupon(null, 0);

  const addToCart = (product: Product) => dispatch({ type: 'ADD_ITEM', product });
  const removeFromCart = (productId: string) => dispatch({ type: 'REMOVE_ITEM', productId });
  const updateQuantity = (productId: string, qty: number) =>
    dispatch({ type: 'UPDATE_QUANTITY', productId, quantity: qty });
  const clearCart = () => { dispatch({ type: 'CLEAR_CART' }); removeCoupon(); };

  return (
    <CartContext.Provider
      value={{
        state,
        dispatch,
        totalItems,
        totalPrice,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        appliedCoupon,
        discountAmount,
        setAppliedCoupon,
        removeCoupon,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
}
